import React, { memo, useCallback } from 'react';

import { Box } from '@mui/material';
import FormGeneratorSwitchCDS from '@sharedCR/coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum } from 'constants/loanActionEditor';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import useFormContext from '../hooks/useFormContext';
import { wrapperClassname } from '../LoanActionItem.constants';
import { DynamicValueInput } from '../LoanActionItems';
import { getOptionCategoryName } from '../LoanActionSelectOption.utils';

import { EMPTY_TASK_TYPES_OPTIONS } from './LoanActionMersRow.constants';

// One MERS loan action: its kind, an optional switch shown only for the kinds it applies to (Register with MERS's
// pre-closing, Transfer with MERS's flow), and the task raised when MERS refuses it. Every MERS action is
// single-entity, so its one row has no delete button.
const LoanActionMersRow = ({
  index,
  rootPath,
  kindLabel,
  kindOptions,
  toggle = null,
  taskTypesOptions = EMPTY_TASK_TYPES_OPTIONS,
  isDisabled = false,
  isEditAllowed,
}) => {
  const { form, setInForm } = useFormContext();

  const fieldPath = `${rootPath}[${index}]`;
  const kindFieldPath = `${fieldPath}.kind`;
  const toggleFieldPath = toggle ? `${fieldPath}.${toggle.fieldName}` : null;
  const failureTaskTypeFieldPath = `${fieldPath}.failureTaskType`;

  const kind = _.get(form, kindFieldPath);
  const toggleValue = toggle ? _.get(form, toggleFieldPath) : null;

  const isToggleHidden = !toggle || !toggle.kinds.includes(kind?.value);

  const onToggle = useCallback(
    (__, checked) => setInForm(toggleFieldPath, checked),
    [setInForm, toggleFieldPath],
  );

  return (
    <Box className={wrapperClassname}>
      <DynamicValueInput
        required
        disabled={isDisabled || !isEditAllowed}
        label={kindLabel}
        name={kindFieldPath}
        options={kindOptions}
        type={FieldTypesEnum.SELECT}
      />

      <FormGeneratorSwitchCDS
        dataTestId={loanManagementTestIds.getMersToggleSwitchId(fieldPath)}
        disabled={isDisabled || !isEditAllowed}
        isHidden={isToggleHidden}
        label={toggle?.label ?? ''}
        name={toggleFieldPath}
        value={!!toggleValue}
        onChange={onToggle}
      />

      <DynamicValueInput
        disabled={isDisabled || !isEditAllowed}
        groupBy={getOptionCategoryName}
        label="Task to create if MERS refuses it"
        name={failureTaskTypeFieldPath}
        optionKey="taskTypeId"
        options={taskTypesOptions}
        type={FieldTypesEnum.SELECT}
      />
    </Box>
  );
};

LoanActionMersRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  kindLabel: PropTypes.string.isRequired,
  kindOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  toggle: PropTypes.shape({
    fieldName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    kinds: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  isDisabled: PropTypes.bool,
  taskTypesOptions: PropTypes.arrayOf(PropTypes.object),
};

export default memo(LoanActionMersRow);
